import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { approveCampaign } from '@features/campaigns/details/services/approveCampaign';
import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys';
import { SuccessMessage } from '@shared/components/Toast/SuccessMessage/SuccessMessage';
import { i18nNameSpace } from '@shared/consts/i18n';
import { CampaignId } from '@shared/types/campaign';

interface UseApproveCampaignProps {
  campaignId: CampaignId;
  campaignName: string;
  handleToggleModalVisibility: () => void;
}

export function useApproveCampaign({ campaignId, campaignName, handleToggleModalVisibility }: UseApproveCampaignProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const queryClient = useQueryClient();

  const {
    mutate: approveCampaignMutation,
    isPending: approveCampaignIsPending,
    isSuccess,
    isError,
  } = useMutation({
    mutationFn: () => approveCampaign(campaignId),
    onSuccess: async () => {
      toast.success(
        <SuccessMessage message={t('api.success.campaign.approved', { ns: i18nNameSpace.API, campaignName })} />,
      );
      handleToggleModalVisibility();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS] });
    },
    onError: () => {
      handleToggleModalVisibility();
    },
  });

  const handleApproveConfirmation = () => {
    approveCampaignMutation();
  };

  const isRequestCompleted = isSuccess || isError;

  return {
    approveCampaignIsPending,
    handleApproveConfirmation,
    isApproveCampaignRequestCompleted: isRequestCompleted,
  };
}
